import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiEdit2 } from 'react-icons/fi';
import { useUserStore } from '../../../../stores/userStore';
import { useWebLocale } from '../../../../contexts/WebLocaleContext';
import { useColorScheme } from '../../../../contexts/ColorSchemeContext';
import baseline, { appTextStyle } from '../../../../app-ui/baseline';
import { saveCurrentPathAndRedirectToLogin } from '../../../../utils/authUtils';
import { reviewRoutePaths } from '../../../../utils/reviewRoutePaths';
import { ReviewRow } from '../user-review/UserReviewSection';
import { RatingStar } from '../album/AlbumActionEditor';
import './AlbumMyReview.css';

// Detail uses its already-loaded album/review state, including a saved zero rating.
export function ownAlbumReview(album, reviews, user) {
  if (!user) return null;
  const saved = album?.myReview;
  return (
    reviews.find((item) =>
      saved?.id ? String(item.id) === String(saved.id) : String(item.user?.id) === String(user.id),
    ) || (saved?.id ? saved : null)
  );
}
export default function AlbumMyReview({
  album,
  reviews = [],
  onCompose,
  onRemove,
  deleting = false,
}) {
  const { user, isLogin } = useUserStore();
  const { locale } = useWebLocale();
  const { resolvedMode } = useColorScheme();
  const navigate = useNavigate();
  const location = useLocation();
  const en = locale === 'en';
  const colors = baseline.tokens[resolvedMode === 'dark' ? 'darkColors' : 'lightColors'];
  const saved = ownAlbumReview(album, reviews, isLogin ? user : null);
  const review = saved?.content?.trim() ? { ...saved, user: { ...saved.user, ...user } } : null;
  const savedRating = Number(saved?.rating ?? album?.userRating ?? 0).toFixed(1);
  const create = () => (isLogin ? onCompose() : saveCurrentPathAndRedirectToLogin(navigate));
  return (
    <section
      className="pp-album-my-review"
      aria-label={en ? 'My Review' : '내 리뷰'}
      style={{
        '--member-text': colors.text,
        '--member-secondary': colors.textSecondary,
        '--member-body': colors.reviewBody,
        '--member-muted': colors.surfaceMuted,
      }}
    >
      <div className="pp-my-review-heading">
        <h2 style={appTextStyle(baseline.tokens.typography.sectionTitle)}>
          {en ? 'My Review' : '내 리뷰'}
        </h2>
        {review && (
          <div className="pp-my-review-actions">
            <button
              type="button"
              onClick={onCompose}
              aria-label={en ? 'Edit my review' : '내 리뷰 수정'}
            >
              {en ? 'Edit' : '수정'}
            </button>
            <button
              type="button"
              className="is-danger"
              onClick={onRemove}
              disabled={deleting}
              aria-label={
                deleting
                  ? en
                    ? 'Deleting my review'
                    : '내 리뷰 삭제 중'
                  : en
                    ? 'Delete my review'
                    : '내 리뷰 삭제'
              }
            >
              {deleting ? (en ? 'Deleting…' : '삭제 중…') : en ? 'Delete' : '삭제'}
            </button>
          </div>
        )}
      </div>
      {review ? (
        <ReviewRow
          review={review}
          basePath={reviewRoutePaths(location.pathname, location.search).list}
          en={en}
          full
          own
        />
      ) : (
        <div className="pp-my-review-create">
          <div className="pp-my-review-copy">
            <p>
              {isLogin
                ? en
                  ? 'You have not written a review yet'
                  : '아직 작성한 리뷰가 없어요'
                : en
                  ? 'Log in to share your thoughts'
                  : '로그인하고 감상을 남겨보세요'}
            </p>
            <p style={appTextStyle(baseline.contentText.metadata)}>
              {isLogin && saved?.id ? (
                <>
                  <RatingStar value={1} size={baseline.contentRating.starSize} />
                  {en
                    ? `Your ${savedRating} rating will remain`
                    : `내 평점 ${savedRating}은 유지됩니다`}
                </>
              ) : en ? (
                'Record what you discovered while listening.'
              ) : (
                '앨범을 들으며 발견한 생각을 기록해 보세요.'
              )}
            </p>
          </div>
          <button
            type="button"
            className="pp-my-review-write"
            onClick={create}
            aria-label={
              isLogin
                ? en
                  ? 'Write an album review'
                  : '이 앨범 리뷰 작성'
                : en
                  ? 'Log in to write a review'
                  : '로그인하고 리뷰 작성'
            }
          >
            <FiEdit2 size={16} />
            {en ? 'Write Review' : '리뷰 작성'}
          </button>
        </div>
      )}
    </section>
  );
}
